import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { ApiError, api } from '@/lib/api';

/**
 * TOTP の登録フロー (setup → confirm)。
 *
 * アカウント画面と、二段階認証が必須のテナントで出す登録画面の両方から
 * 使う。手順書を 2 箇所に持たないため、フロー本体はここ 1 つだけにする。
 *
 * `/api/auth/totp/confirm` はセッションを作り直して `mfa_method` を
 * 'totp' に上げるので、成功したら呼び出し側は `/api/auth/me` を読み直す。
 *
 * **リカバリーコードは自分で表示しない。** 有効化すると `me` が入れ替わり、
 * このコンポーネント自体が別の表示に差し替わる (アカウント画面は「有効」の
 * 表示へ、必須化の登録画面は画面ごと閉じる)。平文が出るのは一度きりなので、
 * 消えない場所 = 呼び出し側に渡して持たせる。
 */
export function TotpSetupFlow({ onEnabled }: { onEnabled: (recoveryCodes: string[]) => void }) {
  const [setup, setSetup] = useState<{ secret: string; uri: string } | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setMessage(null);
    try {
      await action();
    } catch (cause) {
      setMessage(cause instanceof ApiError ? cause.message : '通信に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {setup === null ? (
        <>
          <p className="text-xs leading-relaxed text-ink-subtle">
            {
              '認証アプリで 6 桁のコードを使うようにします。MCP の個人アクセストークンは二段階認証まで通ったセッションでしか発行できません。'
            }
          </p>
          <div>
            <Button
              variant="primary"
              disabled={busy}
              onClick={() =>
                run(async () => {
                  setSetup(await api.post<{ secret: string; uri: string }>('/api/auth/totp/setup'));
                })
              }
            >
              設定を始める
            </Button>
          </div>
        </>
      ) : (
        <>
          <p className="text-xs leading-relaxed text-ink-subtle">
            {'認証アプリに次のシークレットを手入力するか、URI を読み込ませてください。'}
          </p>
          <code className="block break-all border border-line-bold bg-surface-strong px-3 py-2 font-mono text-sm">
            {setup.secret}
          </code>
          <code className="block break-all font-mono text-[11px] text-ink-subtle">{setup.uri}</code>

          <Field label="アプリに表示された 6 桁のコード" className="w-56">
            <Input
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="font-mono"
              disabled={busy}
            />
          </Field>
          <div>
            <Button
              variant="primary"
              disabled={busy || code.length < 6}
              onClick={() =>
                run(async () => {
                  const result = await api.post<{ recoveryCodes: string[] }>(
                    '/api/auth/totp/confirm',
                    { code },
                  );
                  setSetup(null);
                  setCode('');
                  onEnabled(result.recoveryCodes);
                })
              }
            >
              有効にする
            </Button>
          </div>
        </>
      )}
      {message ? <p className="text-sm text-alert">{message}</p> : null}
    </>
  );
}

/** 平文が出るのはこの一度きり。閉じたら二度と表示しない。 */
export function RecoveryCodeList({ codes }: { codes: string[] }) {
  return (
    <div className="flex flex-col gap-2 border border-brand-700 px-4 py-3">
      <span className="text-xs font-bold">リカバリーコード（この画面でしか表示されません）</span>
      <div className="grid grid-cols-2 gap-1 font-mono text-xs">
        {codes.map((code) => (
          <span key={code}>{code}</span>
        ))}
      </div>
    </div>
  );
}
